import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { errMsg } from '../lib/errors';
import { LoadingBlock } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import ImageUpload from '../components/ImageUpload';
import { Plus, Trash2, Save, RotateCcw, Layout, Megaphone, Image as ImageIcon, LayoutGrid, ArrowUp, ArrowDown, Info } from 'lucide-react';
import { toast } from 'sonner';

const emptySlide = () => ({
    id: `new_${Math.random().toString(36).slice(2, 8)}`,
    badge_text: '', heading_line1: '', heading_line2: '',
    highlight_color: 'hsl(142 76% 70%)',
    subheading: '',
    cta_primary_label: '', cta_primary_link: '',
    cta_secondary_label: '', cta_secondary_link: '',
    image_url: '', active: true, sort_order: 100,
});

const emptySection = (type = 'products') => ({
    id: `new_${Math.random().toString(36).slice(2, 8)}`,
    type, title: type === 'products' ? 'New Section' : 'Categories', subtitle: '',
    enabled: true, position: 100, view_all_link: '',
    mode: 'filter', filter: { kind: 'featured', value: null, limit: 8 },
    product_ids: [], category_ids: [], style: 'grid',
});

export default function HomeCMS() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [c, cats] = await Promise.all([
                api.get('/home'),
                api.get('/categories'),
            ]);
            setCfg(c.data);
            setCategories(cats.data);
        } catch (e) { toast.error(errMsg(e, 'Failed to load')); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setTopBar = (patch) => setCfg(c => ({ ...c, top_bar: { ...(c.top_bar || {}), ...patch } }));
    const setSlides = (fn) => setCfg(c => ({ ...c, hero_slides: fn(c.hero_slides || []) }));
    const setSections = (fn) => setCfg(c => ({ ...c, sections: fn(c.sections || []) }));

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                top_bar: cfg.top_bar,
                hero_slides: cfg.hero_slides,
                sections: cfg.sections,
            };
            await api.put('/home', body);
            toast.success('Home CMS saved. Storefront updated.');
        } catch (e) { toast.error(errMsg(e, 'Save failed')); }
        finally { setSaving(false); }
    };
    const resetDefaults = async () => {
        if (!window.confirm('Reset the Home CMS to factory defaults? Your current customisation will be lost.')) return;
        try { const r = await api.post('/home/reset'); setCfg(r.data); toast.success('Reset to defaults'); }
        catch (e) { toast.error(errMsg(e, 'Reset failed')); }
    };

    const moveSlide = (idx, dir) => setSlides(list => {
        const next = [...list]; const swap = idx + dir;
        if (swap < 0 || swap >= next.length) return next;
        [next[idx], next[swap]] = [next[swap], next[idx]];
        return next.map((s, i) => ({ ...s, sort_order: (i + 1) * 10 }));
    });
    const moveSection = (idx, dir) => setSections(list => {
        const next = [...list]; const swap = idx + dir;
        if (swap < 0 || swap >= next.length) return next;
        [next[idx], next[swap]] = [next[swap], next[idx]];
        return next.map((s, i) => ({ ...s, position: (i + 1) * 10 }));
    });
    const updateSlide = (idx, patch) => setSlides(list => list.map((s, i) => i === idx ? { ...s, ...patch } : s));
    const updateSection = (idx, patch) => setSections(list => list.map((s, i) => i === idx ? { ...s, ...patch } : s));
    const updateFilter = (idx, patch) => setSections(list => list.map((s, i) => i === idx ? { ...s, filter: { ...(s.filter || {}), ...patch } } : s));

    // Category toggle helpers (delegates to /categories PATCH)
    const rootCats = categories.filter(c => !c.parent_id);
    const toggleCatOnHome = async (cat, val) => {
        try {
            await api.patch(`/categories/${cat.id}`, { show_on_home: val });
            setCategories(list => list.map(c => c.id === cat.id ? { ...c, show_on_home: val } : c));
        } catch (e) { toast.error(errMsg(e, 'Update failed')); }
    };

    if (loading || !cfg) return <AppShell pageTitle="Home CMS"><LoadingBlock /></AppShell>;

    return (
        <AppShell pageTitle="Home Page CMS" pageActions={
            <div className="flex gap-2">
                <Button variant="outline" onClick={resetDefaults} data-testid="cms-reset-button"><RotateCcw className="h-4 w-4 mr-2" />Reset defaults</Button>
                <Button onClick={save} disabled={saving} data-testid="cms-save-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                    <Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save changes'}
                </Button>
            </div>
        }>
            <div className="rounded-lg border bg-blue-50 border-blue-200 p-3 mb-4 flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 text-blue-700 shrink-0" />
                <div className="text-xs text-blue-900">
                    Configure everything on your customer home page. Changes go live instantly after Save.
                    Slides on mobile auto-rotate every 5 seconds; desktop uses the first slide with the collage animation on the right.
                </div>
            </div>

            <Tabs defaultValue="topbar">
                <TabsList className="mb-4 flex flex-wrap gap-2">
                    <TabsTrigger value="topbar" data-testid="tab-topbar"><Megaphone className="h-4 w-4 mr-1" />Top Bar</TabsTrigger>
                    <TabsTrigger value="hero" data-testid="tab-hero"><ImageIcon className="h-4 w-4 mr-1" />Hero Slides ({cfg.hero_slides?.length || 0})</TabsTrigger>
                    <TabsTrigger value="sections" data-testid="tab-sections"><Layout className="h-4 w-4 mr-1" />Sections ({cfg.sections?.length || 0})</TabsTrigger>
                    <TabsTrigger value="cats" data-testid="tab-cats"><LayoutGrid className="h-4 w-4 mr-1" />Home Categories</TabsTrigger>
                </TabsList>

                {/* ---------- TOP BAR ---------- */}
                <TabsContent value="topbar">
                    <Card className="p-5 max-w-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-sm font-semibold">Announcement Bar</div>
                                <div className="text-xs text-neutral-500">Shows at the very top of every customer page.</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={cfg.top_bar?.enabled} onCheckedChange={v => setTopBar({ enabled: v })} data-testid="topbar-enabled-switch" />
                                <Label>{cfg.top_bar?.enabled ? 'On' : 'Off'}</Label>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div><Label>Message *</Label><Input value={cfg.top_bar?.message || ''} onChange={e => setTopBar({ message: e.target.value })} data-testid="topbar-message-input" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Link label</Label><Input value={cfg.top_bar?.link_label || ''} onChange={e => setTopBar({ link_label: e.target.value })} placeholder="e.g. Shop now" /></div>
                                <div><Label>Link URL</Label><Input value={cfg.top_bar?.link_url || ''} onChange={e => setTopBar({ link_url: e.target.value })} placeholder="e.g. /products" /></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Background color</Label>
                                    <div className="flex gap-2 items-center">
                                        <Input type="color" value={cfg.top_bar?.bg_color || '#166534'} onChange={e => setTopBar({ bg_color: e.target.value })} className="w-16 h-9 p-1" data-testid="topbar-bg-color" />
                                        <Input value={cfg.top_bar?.bg_color || ''} onChange={e => setTopBar({ bg_color: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <Label>Text color</Label>
                                    <div className="flex gap-2 items-center">
                                        <Input type="color" value={cfg.top_bar?.text_color || '#ffffff'} onChange={e => setTopBar({ text_color: e.target.value })} className="w-16 h-9 p-1" />
                                        <Input value={cfg.top_bar?.text_color || ''} onChange={e => setTopBar({ text_color: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            {/* Live preview */}
                            <div className="rounded-md border overflow-hidden">
                                <div style={{ background: cfg.top_bar?.bg_color, color: cfg.top_bar?.text_color }} className="px-4 py-2 text-xs text-center">
                                    {cfg.top_bar?.message} {cfg.top_bar?.link_label && (<span className="ml-2 underline">{cfg.top_bar.link_label} →</span>)}
                                </div>
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                {/* ---------- HERO SLIDES ---------- */}
                <TabsContent value="hero">
                    <div className="flex justify-end mb-3">
                        <Button variant="outline" size="sm" onClick={() => setSlides(l => [...l, emptySlide()])} data-testid="add-hero-slide"><Plus className="h-4 w-4 mr-1" />Add slide</Button>
                    </div>
                    <div className="space-y-4">
                        {(cfg.hero_slides || []).map((s, i) => (
                            <Card key={s.id} className="p-4" data-testid={`hero-slide-${i}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="text-xs font-mono rounded bg-neutral-100 px-2 py-0.5">#{i + 1}</div>
                                        <div className="font-semibold text-sm">{s.heading_line1 || 'Untitled slide'}</div>
                                        <Switch checked={s.active} onCheckedChange={v => updateSlide(i, { active: v })} />
                                        <span className="text-xs text-neutral-500">{s.active ? 'Active' : 'Hidden'}</span>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => moveSlide(i, -1)} disabled={i === 0} data-testid={`hero-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => moveSlide(i, +1)} disabled={i === (cfg.hero_slides.length - 1)} data-testid={`hero-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => setSlides(l => l.filter((_, j) => j !== i))} data-testid={`hero-delete-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <div><Label>Badge (small pill above heading)</Label><Input value={s.badge_text || ''} onChange={e => updateSlide(i, { badge_text: e.target.value })} data-testid={`hero-badge-${i}`} /></div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div><Label>Heading line 1 *</Label><Input value={s.heading_line1 || ''} onChange={e => updateSlide(i, { heading_line1: e.target.value })} data-testid={`hero-h1-${i}`} /></div>
                                            <div><Label>Heading line 2</Label><Input value={s.heading_line2 || ''} onChange={e => updateSlide(i, { heading_line2: e.target.value })} data-testid={`hero-h2-${i}`} /></div>
                                        </div>
                                        <div>
                                            <Label>Highlight colour (for line 2)</Label>
                                            <div className="flex gap-2">
                                                <Input value={s.highlight_color || ''} onChange={e => updateSlide(i, { highlight_color: e.target.value })} placeholder="hsl(142 76% 70%)" />
                                            </div>
                                        </div>
                                        <div><Label>Subheading</Label><Textarea rows={2} value={s.subheading || ''} onChange={e => updateSlide(i, { subheading: e.target.value })} /></div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div><Label>Primary CTA label</Label><Input value={s.cta_primary_label || ''} onChange={e => updateSlide(i, { cta_primary_label: e.target.value })} data-testid={`hero-cta1-label-${i}`} /></div>
                                            <div><Label>Primary CTA link</Label><Input value={s.cta_primary_link || ''} onChange={e => updateSlide(i, { cta_primary_link: e.target.value })} placeholder="/category/plants" /></div>
                                            <div><Label>Secondary CTA label</Label><Input value={s.cta_secondary_label || ''} onChange={e => updateSlide(i, { cta_secondary_label: e.target.value })} /></div>
                                            <div><Label>Secondary CTA link</Label><Input value={s.cta_secondary_link || ''} onChange={e => updateSlide(i, { cta_secondary_link: e.target.value })} /></div>
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Mobile hero image *</Label>
                                        <ImageUpload
                                            value={s.image_url || ''}
                                            folder="banners"
                                            testId={`hero-image-${i}`}
                                            onChange={(url) => updateSlide(i, { image_url: url })}
                                            size="lg"
                                        />
                                        <div className="text-xs text-neutral-500 mt-2">Shown on mobile hero slider (portrait orientation works best).</div>

                                        <div className="mt-4">
                                            <Label>Desktop hero image (optional)</Label>
                                            <ImageUpload
                                                value={s.desktop_image_url || ''}
                                                folder="banners"
                                                testId={`hero-desktop-image-${i}`}
                                                onChange={(url) => updateSlide(i, { desktop_image_url: url })}
                                                size="lg"
                                            />
                                            <div className="text-xs text-neutral-500 mt-2">Shown on desktop hero (right side). If left empty, the animated collage is shown.</div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                        {(cfg.hero_slides || []).length === 0 && (
                            <Card className="p-8 text-center text-sm text-neutral-500">No hero slides yet. Click "Add slide" to create one.</Card>
                        )}
                    </div>
                </TabsContent>

                {/* ---------- SECTIONS ---------- */}
                <TabsContent value="sections">
                    <div className="flex justify-end mb-3 gap-2">
                        <Button variant="outline" size="sm" onClick={() => setSections(l => [...l, emptySection('categories')])} data-testid="add-section-categories"><Plus className="h-4 w-4 mr-1" />Add Categories block</Button>
                        <Button variant="outline" size="sm" onClick={() => setSections(l => [...l, emptySection('products')])} data-testid="add-section-products"><Plus className="h-4 w-4 mr-1" />Add Products block</Button>
                    </div>
                    <div className="space-y-4">
                        {(cfg.sections || []).map((s, i) => (
                            <Card key={s.id} className="p-4" data-testid={`section-${i}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="text-xs font-mono rounded bg-neutral-100 px-2 py-0.5">pos&nbsp;{s.position}</div>
                                        <span className={`text-[10px] font-semibold uppercase tracking-wider rounded px-2 py-0.5 ${s.type === 'products' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{s.type}</span>
                                        <span className="font-semibold text-sm">{s.title}</span>
                                        <Switch checked={s.enabled} onCheckedChange={v => updateSection(i, { enabled: v })} />
                                        <span className="text-xs text-neutral-500">{s.enabled ? 'On' : 'Off'}</span>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => moveSection(i, -1)} disabled={i === 0} data-testid={`section-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => moveSection(i, +1)} disabled={i === cfg.sections.length - 1} data-testid={`section-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => setSections(l => l.filter((_, j) => j !== i))} data-testid={`section-delete-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    <div><Label>Section title *</Label><Input value={s.title} onChange={e => updateSection(i, { title: e.target.value })} data-testid={`section-title-${i}`} /></div>
                                    <div><Label>Subtitle</Label><Input value={s.subtitle || ''} onChange={e => updateSection(i, { subtitle: e.target.value })} /></div>
                                    <div><Label>View-all link (optional)</Label><Input value={s.view_all_link || ''} onChange={e => updateSection(i, { view_all_link: e.target.value })} placeholder="/products?featured=true" /></div>
                                    <div><Label>Position</Label><Input type="number" value={s.position} onChange={e => updateSection(i, { position: parseInt(e.target.value) || 100 })} data-testid={`section-position-${i}`} /></div>
                                </div>
                                {s.type === 'products' && (
                                    <div className="mt-3 rounded-md border bg-neutral-50/60 p-3 space-y-3">
                                        <div className="flex items-center gap-4">
                                            <Label>Source</Label>
                                            <label className="text-sm flex items-center gap-1"><input type="radio" name={`mode-${i}`} checked={s.mode === 'filter'} onChange={() => updateSection(i, { mode: 'filter' })} /> Filter</label>
                                            <label className="text-sm flex items-center gap-1"><input type="radio" name={`mode-${i}`} checked={s.mode === 'manual'} onChange={() => updateSection(i, { mode: 'manual' })} /> Manual</label>
                                        </div>
                                        {s.mode === 'filter' && (
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                <div>
                                                    <Label>Filter kind</Label>
                                                    <Select value={s.filter?.kind || 'featured'} onValueChange={v => updateFilter(i, { kind: v })}>
                                                        <SelectTrigger data-testid={`section-filter-kind-${i}`}><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="featured">Featured only</SelectItem>
                                                            <SelectItem value="newest">Newest arrivals</SelectItem>
                                                            <SelectItem value="bestseller">Best sellers</SelectItem>
                                                            <SelectItem value="by_category">By category</SelectItem>
                                                            <SelectItem value="by_tag">By tag</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                {s.filter?.kind === 'by_category' && (
                                                    <div>
                                                        <Label>Category</Label>
                                                        <Select value={s.filter?.value || ''} onValueChange={v => updateFilter(i, { value: v })}>
                                                            <SelectTrigger><SelectValue placeholder="Pick a category" /></SelectTrigger>
                                                            <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                                                        </Select>
                                                    </div>
                                                )}
                                                {s.filter?.kind === 'by_tag' && (
                                                    <div><Label>Tag</Label><Input value={s.filter?.value || ''} onChange={e => updateFilter(i, { value: e.target.value })} placeholder="e.g. flowering" /></div>
                                                )}
                                                <div><Label>Number of products</Label><Input type="number" value={s.filter?.limit ?? 8} onChange={e => updateFilter(i, { limit: parseInt(e.target.value) || 8 })} data-testid={`section-limit-${i}`} /></div>
                                            </div>
                                        )}
                                        {s.mode === 'manual' && (
                                            <div>
                                                <Label>Product IDs (comma-separated)</Label>
                                                <Textarea rows={2} value={(s.product_ids || []).join(', ')} onChange={e => updateSection(i, { product_ids: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} placeholder="paste product ids…" data-testid={`section-manual-${i}`} />
                                                <div className="text-xs text-neutral-500 mt-1">Order matters. Find IDs in the Products list.</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {s.type === 'categories' && (
                                    <div className="mt-3 rounded-md border bg-neutral-50/60 p-3">
                                        <div className="text-xs text-neutral-600 mb-2">Leave "Categories to show" empty to auto-list all root categories that are marked "Show on home". Otherwise, pick specific categories:</div>
                                        <div className="flex flex-wrap gap-1">
                                            {rootCats.map(c => {
                                                const on = (s.category_ids || []).includes(c.id);
                                                return (
                                                    <button key={c.id} type="button" onClick={() => updateSection(i, { category_ids: on ? s.category_ids.filter(x => x !== c.id) : [...(s.category_ids || []), c.id] })} className={`rounded-full text-xs px-3 py-1 border ${on ? 'bg-green-100 text-green-800 border-green-300' : 'bg-white text-neutral-700'}`}>
                                                        {c.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                {/* ---------- HOME CATEGORY TOGGLES ---------- */}
                <TabsContent value="cats">
                    <Card className="p-5 max-w-3xl">
                        <div className="text-sm font-semibold mb-1">Show on Home Page</div>
                        <div className="text-xs text-neutral-500 mb-4">Toggle which root categories appear on the customer home page "Shop by Category" grid (when a Categories section is set to auto). Reorder via each category's sort order on the Categories admin page.</div>
                        <div className="space-y-2">
                            {rootCats.map(c => (
                                <div key={c.id} className="flex items-center justify-between border rounded-md p-2" data-testid={`home-cat-toggle-${c.id}`}>
                                    <div className="flex items-center gap-3">
                                        {c.image && <img src={c.image} alt="" className="h-9 w-9 rounded object-cover border" onError={(e) => e.currentTarget.style.display = 'none'} />}
                                        <div>
                                            <div className="font-medium text-sm">{c.name}</div>
                                            <div className="text-xs text-neutral-500">order: {c.sort_order ?? 0}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch checked={c.show_on_home !== false} onCheckedChange={v => toggleCatOnHome(c, v)} />
                                        <span className="text-xs text-neutral-500 w-14">{c.show_on_home !== false ? 'Visible' : 'Hidden'}</span>
                                    </div>
                                </div>
                            ))}
                            {rootCats.length === 0 && <div className="text-sm text-neutral-500">No root categories yet.</div>}
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>
        </AppShell>
    );
}
